import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Ticket, Plus, Trash2, Save, X, RefreshCw, Eye, EyeOff } from 'lucide-react';
import { useAdminAuth } from '../context/AdminAuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = 'w-full bg-white dark:bg-[#0f0f10] border border-neutral-200 dark:border-neutral-800 rounded-md px-3 py-2 text-[13.5px] outline-none focus:border-neutral-400';
const empty = { code: '', discount_type: 'percent', value: '', is_active: true, expires_at: '', max_uses: 0, min_order: 0, one_per_customer: false, description: '', category_ids: [] };

const fmtValue = (c) => (c.discount_type === 'percent' ? `${Number(c.value)}%` : `$${Number(c.value).toFixed(2)}`);
const isExpired = (c) => c.expires_at && new Date(c.expires_at.length <= 10 ? `${c.expires_at}T23:59:59Z` : c.expires_at) < new Date();

const Lbl = ({ label, children }) => (
  <label className="block">
    <span className="text-[11px] tracking-widest uppercase text-neutral-500">{label}</span>
    {children}
  </label>
);

const CouponForm = ({ editing, setEditing, onSave, saving, error, catOptions }) => {
  const upd = (k, v) => setEditing(prev => ({ ...prev, [k]: v }));
  const sel = editing.category_ids || [];
  const toggleCat = (id) => upd('category_ids', sel.includes(id) ? sel.filter(x => x !== id) : [...sel, id]);
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 backdrop-blur-sm p-4 overflow-y-auto" onClick={() => setEditing(null)}>
      <div data-testid="coupon-form-modal" className="mt-16 w-full max-w-lg bg-white dark:bg-[#141416] rounded-xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200 dark:border-neutral-800">
          <h3 className="font-serif text-[18px] text-neutral-900 dark:text-neutral-100">{editing.isNew ? 'Add coupon' : 'Edit coupon'}</h3>
          <button onClick={() => setEditing(null)} className="p-1.5 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          <Lbl label="Code">
            <input data-testid="coupon-code-input" disabled={!editing.isNew} className={inputCls + ' font-mono uppercase' + (editing.isNew ? '' : ' opacity-60')} value={editing.code} onChange={(e) => upd('code', e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ''))} placeholder="e.g. WELCOME10" />
          </Lbl>
          <div className="grid grid-cols-2 gap-3">
            <Lbl label="Type">
              <select data-testid="coupon-type-select" className={inputCls} value={editing.discount_type} onChange={(e) => upd('discount_type', e.target.value)}>
                <option value="percent">Percentage (%)</option>
                <option value="fixed">Fixed amount ($)</option>
              </select>
            </Lbl>
            <Lbl label={editing.discount_type === 'percent' ? 'Percent off' : 'Amount off ($)'}>
              <input data-testid="coupon-value-input" type="number" min="0" step="0.01" className={inputCls} value={editing.value} onChange={(e) => upd('value', e.target.value)} />
            </Lbl>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Lbl label="Min order ($)">
              <input data-testid="coupon-min-order-input" type="number" min="0" step="0.01" className={inputCls} value={editing.min_order} onChange={(e) => upd('min_order', e.target.value)} />
            </Lbl>
            <Lbl label="Max uses (0 = ∞)">
              <input data-testid="coupon-max-uses-input" type="number" min="0" step="1" className={inputCls} value={editing.max_uses} onChange={(e) => upd('max_uses', e.target.value)} />
            </Lbl>
          </div>
          <Lbl label="Expires on (optional)">
            <input data-testid="coupon-expiry-input" type="date" className={inputCls} value={(editing.expires_at || '').slice(0, 10)} onChange={(e) => upd('expires_at', e.target.value)} />
          </Lbl>
          <div>
            <span className="text-[11px] tracking-widest uppercase text-neutral-500">Restrict to categories <span className="normal-case tracking-normal text-neutral-400">— none selected = whole cart</span></span>
            <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="coupon-category-list">
              {catOptions.map(c => (
                <button type="button" key={c.id} data-testid={`coupon-cat-${c.id}`} onClick={() => toggleCat(c.id)} className={`px-2.5 py-1 rounded-full text-[11.5px] border transition-colors ${sel.includes(c.id) ? 'bg-emerald-600 border-emerald-600 text-white' : 'border-neutral-300 dark:border-neutral-700 text-neutral-600 dark:text-neutral-300 hover:border-neutral-500'}`}>{c.label}</button>
              ))}
            </div>
            {sel.length > 0 && <p className="text-[11px] text-neutral-500 mt-1">Discount & minimum order use only items from these categories. Stacks and kits are excluded.</p>}
          </div>
          <Lbl label="Internal note">
            <input data-testid="coupon-description-input" className={inputCls} value={editing.description} onChange={(e) => upd('description', e.target.value)} placeholder="e.g. Summer promo" />
          </Lbl>
          <div className="flex flex-wrap gap-5 pt-1">
            <label className="inline-flex items-center gap-2 text-[13px]">
              <input data-testid="coupon-one-per-customer" type="checkbox" checked={!!editing.one_per_customer} onChange={(e) => upd('one_per_customer', e.target.checked)} className="w-4 h-4 accent-emerald-600" />
              One use per customer
            </label>
            <label className="inline-flex items-center gap-2 text-[13px]">
              <input data-testid="coupon-active-checkbox" type="checkbox" checked={!!editing.is_active} onChange={(e) => upd('is_active', e.target.checked)} className="w-4 h-4 accent-emerald-600" />
              Active
            </label>
          </div>
          {error && <div data-testid="coupon-form-error" className="rounded-md border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800 px-3 py-2 text-[12.5px] text-red-700 dark:text-red-300">{error}</div>}
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-neutral-200 dark:border-neutral-800">
          <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-md border border-neutral-200 dark:border-neutral-700 text-[13.5px]">Cancel</button>
          <button data-testid="coupon-save-btn" onClick={onSave} disabled={saving || !editing.code || !editing.value} className="btn-brand inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-[13.5px] font-semibold disabled:opacity-60"><Save size={14} /> {saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
};

const CouponsPanel = () => {
  const { password } = useAdminAuth();
  const headers = useMemo(() => ({ 'X-Admin-Password': password }), [password]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [catOptions, setCatOptions] = useState([]);

  useEffect(() => {
    axios.get(`${API}/admin/categories`, { headers })
      .then(({ data }) => setCatOptions((data || []).map(c => ({ id: c.id, label: c.display || c.label || c.id }))))
      .catch(() => {});
  }, [headers]);
  const catName = (id) => catOptions.find(c => c.id === id)?.label || id;

  const load = useCallback(async () => {
    setLoading(true); setMsg('');
    try { setRows((await axios.get(`${API}/admin/coupons`, { headers })).data); }
    catch (e) { setMsg('Failed to load: ' + (e?.response?.data?.detail || e.message)); }
    finally { setLoading(false); }
  }, [headers]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true); setMsg('');
    const payload = {
      discount_type: editing.discount_type,
      value: Number(editing.value),
      is_active: !!editing.is_active,
      expires_at: editing.expires_at || null,
      max_uses: parseInt(editing.max_uses, 10) || 0,
      min_order: Number(editing.min_order) || 0,
      one_per_customer: !!editing.one_per_customer,
      description: editing.description || '',
      category_ids: editing.category_ids || [],
    };
    try {
      if (editing.isNew) await axios.post(`${API}/admin/coupons`, { ...payload, code: editing.code }, { headers });
      else await axios.patch(`${API}/admin/coupons/${editing.id}`, payload, { headers });
      setEditing(null); await load();
    } catch (e) { setMsg('Save failed: ' + (e?.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete coupon "${c.code}"?`)) return;
    try { await axios.delete(`${API}/admin/coupons/${c.id}`, { headers }); await load(); }
    catch { setMsg('Delete failed'); }
  };

  const toggle = async (c) => {
    try { await axios.patch(`${API}/admin/coupons/${c.id}`, { is_active: !c.is_active }, { headers }); await load(); }
    catch { setMsg('Toggle failed'); }
  };

  return (
    <div className="space-y-4" data-testid="coupons-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-serif text-[24px] text-neutral-900 dark:text-neutral-100 inline-flex items-center gap-2"><Ticket size={20} className="text-neutral-400" /> Coupons</h2>
          <p className="text-[12.5px] text-neutral-500">Discounts apply to the product subtotal only — shipping is never discounted.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="inline-flex items-center gap-1.5 border border-neutral-200 dark:border-neutral-700 rounded-md px-3 py-2 text-[13px]"><RefreshCw size={13} /> Refresh</button>
          <button data-testid="add-coupon-btn" onClick={() => { setMsg(''); setEditing({ ...empty, isNew: true }); }} className="btn-brand inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-[13.5px] font-semibold"><Plus size={14} /> Add coupon</button>
        </div>
      </div>
      {msg && !editing && <div className="text-red-600 text-[13px]" data-testid="coupons-error">{msg}</div>}

      <div className="rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-[#141416] overflow-x-auto">
        <div className="min-w-[760px]">
          <div className="grid grid-cols-12 gap-3 px-4 py-2.5 text-[10.5px] uppercase tracking-widest text-neutral-500 border-b border-neutral-200 dark:border-neutral-800">
            <div className="col-span-3">Code</div><div className="col-span-1">Off</div><div className="col-span-2">Min order</div>
            <div className="col-span-2">Uses</div><div className="col-span-2">Expires</div><div className="col-span-1 text-center">Status</div><div className="col-span-1 text-right">Actions</div>
          </div>
          {loading ? (
            <div className="p-6 text-center text-neutral-500 text-[13.5px]">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-6 text-center text-neutral-500 text-[13.5px]">No coupons yet. Create one to reward customers.</div>
          ) : rows.map((c) => (
            <div key={c.id} data-testid={`coupon-row-${c.code}`} className="grid grid-cols-12 gap-3 px-4 py-3 items-center border-b border-neutral-100 dark:border-neutral-800 last:border-b-0 text-[13px]">
              <div className="col-span-3 min-w-0">
                <div className="font-mono font-semibold text-neutral-900 dark:text-neutral-100 truncate">{c.code}</div>
                <div className="text-[11px] text-neutral-500 truncate">{c.one_per_customer ? 'One per customer' : 'Reusable'}{c.category_ids?.length ? ` · ${c.category_ids.map(catName).join(', ')}` : ' · All products'}{c.description ? ` · ${c.description}` : ''}</div>
              </div>
              <div className="col-span-1 font-semibold text-emerald-600">{fmtValue(c)}</div>
              <div className="col-span-2">{Number(c.min_order) > 0 ? `$${Number(c.min_order).toFixed(2)}` : '—'}</div>
              <div className="col-span-2" data-testid={`coupon-uses-${c.code}`}>{c.uses || 0} / {c.max_uses ? c.max_uses : '∞'}</div>
              <div className={`col-span-2 ${isExpired(c) ? 'text-red-500' : ''}`}>{c.expires_at ? c.expires_at.slice(0, 10) : 'Never'}{isExpired(c) ? ' (expired)' : ''}</div>
              <div className="col-span-1 flex justify-center">
                <button data-testid={`coupon-toggle-${c.code}`} onClick={() => toggle(c)} className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium ${c.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-neutral-200 text-neutral-600'}`}>
                  {c.is_active ? <Eye size={11} /> : <EyeOff size={11} />} {c.is_active ? 'Active' : 'Off'}
                </button>
              </div>
              <div className="col-span-1 flex justify-end gap-1">
                <button data-testid={`coupon-edit-${c.code}`} onClick={() => { setMsg(''); setEditing({ ...c, isNew: false, expires_at: c.expires_at || '', category_ids: c.category_ids || [] }); }} className="px-2 py-1 text-[12px] rounded-md border border-neutral-200 dark:border-neutral-700">Edit</button>
                <button data-testid={`coupon-delete-${c.code}`} onClick={() => remove(c)} className="p-1.5 rounded-md text-neutral-500 hover:text-red-500"><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {editing && <CouponForm editing={editing} setEditing={(v) => { setEditing(v); if (!v) setMsg(''); }} onSave={save} saving={saving} error={msg} catOptions={catOptions} />}
    </div>
  );
};

export default CouponsPanel;
